import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { findByTitulo, findByCategoria } from "./api-comunicado-usuario";
import { findAll } from "../ComunicadoCategoria/api-comunicado-categoria";
import TablePaginationActions from "../../../shared/components/Table/Actions";
import { dateTimeFormat } from "../../../util/DateUtil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";

const TITULO_KEY = "titulo";
const CATEGORIA_KEY = "categoria";

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

export default function ComunicadoUsuarioFindDialog({
  openSearchComunicado,
  setOpenSearchComunicado,
  setComunicadoUsuario,
}) {
  const [listCU, setListCU] = useState(EMPTY_PAGE);
  const [por, setPor] = useState(TITULO_KEY);
  const [busca, setBusca] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    findAll().then((data) => {
      if (Array.isArray(data)) {
        setCategorias(data);
      } else if (data?.error) {
        errorMessage(data.message);
      }
    });
  }, []);

  const fetchData = (newPage) => {
    const request = por === TITULO_KEY ? findByTitulo(busca, newPage) : findByCategoria(busca, newPage);
    setIsLoading(true);
    request
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setListCU(data);
          setHasSearched(true);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    if (!busca) {
      errorToast(por === TITULO_KEY ? "Informe o título." : "Selecione a categoria.");
      return;
    }
    fetchData(0);
  };

  const handlePorChange = (event) => {
    setPor(event.target.value);
    setBusca("");
  };

  const handleClose = () => {
    setOpenSearchComunicado(false);
  };

  const handleSelected = (comunicadoUsuario) => {
    setComunicadoUsuario(comunicadoUsuario);
    handleClose();
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <Box py={4}>
          <Loading description="Carregando Comunicados..." />
        </Box>
      );
    }
    if (!hasSearched) return null;
    if (listCU.content.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
          Nenhum comunicado encontrado.
        </Typography>
      );
    }
    return (
      <>
        <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
          <Table size="small" aria-label="Resultado da busca de comunicados">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
                <TableCell>Título</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Enviado em</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listCU.content.map((cu) => (
                <TableRow key={cu.codigo} hover onClick={() => handleSelected(cu)} sx={{ cursor: "pointer" }}>
                  <TableCell>{cu.comunicado.titulo}</TableCell>
                  <TableCell>{cu.comunicado.categoria?.descricao ?? ""}</TableCell>
                  <TableCell>{dateTimeFormat(cu.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          rowsPerPage={listCU.size || 0}
          component="div"
          count={listCU.totalElements}
          page={listCU.number}
          onPageChange={(event, newPage) => fetchData(newPage)}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? `${count} registros` : `mais de ${to}`}`
          }
          ActionsComponent={TablePaginationActions}
        />
      </>
    );
  };

  return (
    <Dialog
      open={openSearchComunicado}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="comunicado-usuario-find-dialog-title"
    >
      <DialogTitle id="comunicado-usuario-find-dialog-title">Procurar Comunicados</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <form onSubmit={clickSubmit}>
            <Stack spacing={2}>
              <FormControl>
                <RadioGroup row name="por" value={por} onChange={handlePorChange}>
                  <FormControlLabel value={TITULO_KEY} control={<Radio />} label="Título" />
                  <FormControlLabel value={CATEGORIA_KEY} control={<Radio />} label="Categoria" />
                </RadioGroup>
              </FormControl>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <Box flexGrow={1}>
                  {por === TITULO_KEY ? (
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      label="Título"
                      value={busca}
                      onChange={(event) => setBusca(event.target.value)}
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel id="busca-categoria-label">Categoria</InputLabel>
                      <Select
                        labelId="busca-categoria-label"
                        label="Categoria"
                        value={busca}
                        onChange={(event) => setBusca(event.target.value)}
                      >
                        {categorias.map((categoria) => (
                          <MenuItem key={categoria.codigo} value={categoria.codigo}>
                            {categoria.descricao}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={isLoading}>
                  Buscar
                </Button>
              </Stack>
            </Stack>
          </form>

          {renderResults()}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
